/**
 * RoomCheckin (PPL-34 → redesigned PPL-68 / C8) — periodic lightweight
 * between-sessions check-in.
 *
 * Per the C8 canvas: four equal-weight options with coloured signal dots,
 * a contextual response line that updates with the selection, and a
 * primary gradient "save my answer" CTA. Voice: lowercase, plain, no
 * stoplight metaphors.
 *
 * Privacy invariant: the other side is asked separately and answers are
 * NEVER shown as a comparison — copy makes that explicit ("sarah is being
 * asked separately. i hold both answers and won't surface them as a
 * comparison.").
 *
 * Data:
 *  - Writes via `submitRoomCheckin` → `submitMediationCheckinEurW`.
 *  - On success, brief flash then navigate(-1).
 */

import React, { CSSProperties, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { submitRoomCheckin } from '../services/roomService';
import type { RoomCheckinAnswer } from '../types/room';
import SEO from '../components/SEO';

// ─── Copy + signal-dot palette ───────────────────────────────

interface OptionDef {
  answer: RoomCheckinAnswer;
  head: string;
  sub: string;
  /** Soft signal-dot colour, matching the canvas. */
  dot: string;
  /** Inline response line shown when this option is picked. */
  response: string;
}

const OPTIONS: OptionDef[] = [
  {
    answer: 'working',
    head: 'this is working',
    sub: 'i want to keep going at this pace',
    dot: '#86efac',
    response:
      'good — noted. nothing changes; you can revisit this any time.',
  },
  {
    answer: 'change',
    head: "i'd like to slow down or change something",
    sub: 'something specific feels off — i want to name it',
    dot: '#fcd34d',
    response:
      "noted. nothing changes unless you raise it — step aside any time to talk through what feels off with f8.",
  },
  {
    answer: 'pause',
    head: 'i want to pause the room',
    sub: "not done, just need air. the other side is told you're pausing, not why",
    dot: '#a5b4fc',
    response:
      "i'll pause the room. the other side sees a pause, not a reason. the room remains; nothing is deleted.",
  },
  {
    answer: 'done',
    head: "i think we're done here",
    sub: 'this was the work. ending now is a clean ending',
    dot: '#f0abfc',
    response:
      "okay — let's go to the ending screen, where you choose what to keep.",
  },
];

// ─── Atoms ───────────────────────────────────────────────────

function DualAurora() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-22%',
          left: '-12%',
          width: '70%',
          height: '70%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #fb923c 0%, transparent 65%)',
          filter: 'blur(85px)',
          opacity: 0.4,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-30%',
          right: '-12%',
          width: '68%',
          height: '72%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #6366f1 0%, transparent 65%)',
          filter: 'blur(95px)',
          opacity: 0.42,
        }}
      />
    </div>
  );
}

function CheckOption({
  on,
  onPick,
  head,
  sub,
  dot,
  disabled,
}: {
  on: boolean;
  onPick: () => void;
  head: string;
  sub: string;
  dot: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      aria-pressed={on}
      style={{
        appearance: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        width: '100%',
        padding: '14px 16px',
        borderRadius: 14,
        background: on ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)',
        border: on ? '1px solid rgba(217,70,239,0.4)' : '1px solid rgba(255,255,255,0.08)',
        boxShadow: on ? '0 0 20px rgba(217,70,239,0.1)' : 'none',
        textAlign: 'left',
        fontFamily: 'inherit',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        opacity: disabled && !on ? 0.55 : 1,
      }}
    >
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: dot,
          marginTop: 7,
          flexShrink: 0,
          boxShadow: `0 0 8px ${dot}`,
        }}
        aria-hidden="true"
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14.5,
            fontWeight: 600,
            color: '#fff',
            letterSpacing: '-0.005em',
          }}
        >
          {head}
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: 'rgba(232,220,250,0.6)',
            marginTop: 3,
            lineHeight: 1.5,
          }}
        >
          {sub}
        </div>
      </div>
      <div
        aria-hidden="true"
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: on ? '5px solid #d946ef' : '1.5px solid rgba(255,255,255,0.25)',
          background: on ? '#fff' : 'transparent',
          marginTop: 3,
          flexShrink: 0,
        }}
      />
    </button>
  );
}

function InlineRailCard({
  eyebrow,
  children,
}: {
  eyebrow: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        position: 'relative',
        maxWidth: 720,
        margin: '24px auto 0',
        paddingLeft: 22,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          top: 6,
          bottom: 4,
          width: 3,
          borderRadius: 3,
          background: 'linear-gradient(180deg, #d946ef, #a855f7)',
        }}
      />
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: '#f0abfc',
          marginBottom: 14,
        }}
      >
        {eyebrow}
      </div>
      {children}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────

export default function RoomCheckin() {
  const { mediationId } = useParams<{ mediationId: string }>();
  const navigate = useNavigate();

  const [selected, setSelected] = useState<RoomCheckinAnswer | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const selectedOpt = OPTIONS.find((o) => o.answer === selected) ?? null;

  const handleSubmit = useCallback(async () => {
    if (!selected || !mediationId || submitting) return;
    // "done" isn't a check-in answer to store — it means "I want to end". Send
    // them to the ending screen to choose what to keep (summary / pause /
    // nothing). Nothing about a summary is promised here.
    if (selected === 'done') {
      navigate(`/rooms/${mediationId}/end`);
      return;
    }
    setSubmitting(true);
    setServerError(null);
    try {
      await submitRoomCheckin(mediationId, selected);
      setSaved(true);
      setTimeout(() => navigate(-1), 1400);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'something went wrong — please try again';
      setServerError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [selected, mediationId, submitting, navigate]);

  const containerStyle: CSSProperties = {
    position: 'relative',
    // Fill the shell's <main> and let the body scroll, rather than a full-viewport
    // min-height that overflows its slot (same fix as the other room screens).
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    background: '#0a0510',
    color: '#fff',
    fontFamily: "'Inter', system-ui, sans-serif",
    overflow: 'hidden',
  };

  if (saved) {
    return (
      <div
        style={{
          ...containerStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SEO title="Check in · f8" />
        <DualAurora />
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            textAlign: 'center',
            color: 'rgba(232,220,250,0.85)',
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #d946ef 0%, #a855f7 100%)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 14,
              boxShadow: '0 8px 24px rgba(217,70,239,0.32)',
            }}
            aria-hidden="true"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12.5l4.5 4.5L19 7"
                stroke="#fff"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>check-in saved</div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <SEO title="Check in · f8" />
      <DualAurora />

      {/* top bar */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 24px 12px',
        }}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="back"
          style={{
            appearance: 'none',
            cursor: 'pointer',
            border: 0,
            padding: '6px 8px 6px 0',
            background: 'transparent',
            color: 'rgba(232,220,250,0.6)',
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M14.5 5l-7 7 7 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          back
        </button>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'rgba(232,220,250,0.4)',
          }}
        >
          check-in · between sessions
        </div>
        <div style={{ width: 40 }} />
      </div>

      {/* body — the scroll surface; top bar stays fixed above it */}
      <main
        style={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          width: '100%',
          padding: '20px 28px 80px',
          maxWidth: 820,
          margin: '0 auto',
        }}
      >
        <div style={{ maxWidth: 720, margin: '4px auto 6px' }}>
          <h1
            style={{
              margin: '0 0 10px',
              fontSize: 26,
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '-0.028em',
              lineHeight: 1.14,
            }}
          >
            a quick check-in
          </h1>
          <p
            style={{
              margin: 0,
              maxWidth: 560,
              fontSize: 14.5,
              lineHeight: 1.55,
              color: 'rgba(232,220,250,0.6)',
            }}
          >
            a moment to feel out how this is going for you. no pressure — your answer stays private.
          </p>
        </div>

        <InlineRailCard eyebrow="how is the room feeling for you?">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 620 }}>
            {OPTIONS.map((opt) => (
              <CheckOption
                key={opt.answer}
                on={selected === opt.answer}
                onPick={() => setSelected(opt.answer)}
                head={opt.head}
                sub={opt.sub}
                dot={opt.dot}
                disabled={submitting}
              />
            ))}
          </div>

          {selectedOpt && (
            <div
              style={{
                marginTop: 18,
                padding: '14px 16px',
                borderRadius: 14,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.09)',
                fontSize: 13.5,
                color: 'rgba(232,220,250,0.78)',
                lineHeight: 1.6,
                maxWidth: 620,
              }}
            >
              {selectedOpt.response}
            </div>
          )}

          <div style={{ marginTop: 20, maxWidth: 620 }}>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!selected || submitting}
              style={{
                appearance: 'none',
                border: 0,
                cursor: selected && !submitting ? 'pointer' : 'not-allowed',
                padding: '12px 22px',
                borderRadius: 999,
                background: selected
                  ? 'linear-gradient(135deg, #d946ef 0%, #a855f7 100%)'
                  : 'rgba(255,255,255,0.06)',
                color: '#fff',
                fontSize: 13.5,
                fontWeight: 700,
                fontFamily: 'inherit',
                boxShadow: selected ? '0 8px 24px rgba(217,70,239,0.32)' : 'none',
                opacity: selected ? (submitting ? 0.7 : 1) : 0.6,
              }}
            >
              {submitting ? 'saving…' : 'save my answer'}
            </button>
          </div>

          {serverError && (
            <div
              role="alert"
              style={{
                marginTop: 14,
                padding: '10px 14px',
                borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.25)',
                fontSize: 13,
                color: '#fca5a5',
                maxWidth: 620,
              }}
            >
              {serverError}
            </div>
          )}
        </InlineRailCard>

        <div
          style={{
            marginTop: 26,
            maxWidth: 720,
            margin: '26px auto 0',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 9,
            fontSize: 12.5,
            lineHeight: 1.55,
            color: 'rgba(232,220,250,0.55)',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
            <path
              d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3Z"
              stroke="rgba(217,70,239,0.6)"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
          </svg>
          <span>
            the other side is never told which answer you picked. checking in nudges them to check in too; pause and done change the room for both of you.
          </span>
        </div>
      </main>
    </div>
  );
}
